// 使用相對路徑 (Vercel 環境會自動導向 /api)
const API_BASE = '';

// 導入離線儲存
import offlineStorage from '../offlineStorage';

// API 服務模組（支援離線模式）
const api = {
  /**
   * 取得店鋪列表
   */
  async getLocations() {
    const response = await fetch(`/api/locations`);
    return response.json();
  },

  /**
   * 取得員工名單
   */
  async getEmployees() {
    const response = await fetch(`/api/employees`);
    return response.json();
  },

   /**
    * 送出註冊申請（姓名 + 電話）→ Telegram 審核
    */
   async register(data) {
     const response = await fetch(`/api/register`, {
       method: 'POST',
       headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify(data)
     });
     
     if (!response.ok) {
       // 嘗試解析錯誤訊息
       let errorMessage = '網路錯誤，請重試';
       try {
         const errorData = await response.json();
         if (errorData.message) {
           errorMessage = errorData.message;
         }
       } catch (e) {
         // 如果無法解析 JSON，使用 HTTP 狀態碼
         errorMessage = `伺服器錯誤 (${response.status})`;
       }
       throw new Error(errorMessage);
     }
     
     return response.json();
   },

  /**
   * 查詢註冊審核狀態
   */
  async getRegistration(id) {
    const response = await fetch(`/api/register/${encodeURIComponent(id)}`);
    return response.json();
  },

  /**
   * 取得當日打卡狀態
   */
  async getStatus(employeeName) {
    const response = await fetch(`/api/status/${encodeURIComponent(employeeName)}`);
    return response.json();
  },

  /**
   * 取得當日工時
   */
  async getWorkHours(employeeName) {
    const response = await fetch(`/api/work-hours/${encodeURIComponent(employeeName)}`);
    return response.json();
  },

  /**
   * 執行打卡（支援離線模式）
   */
  async clockIn(data) {
    // 離線模式下儲存到本地
    if (!navigator.onLine) {
      try {
        const offlineRecord = await offlineStorage.saveClockRecord({
          ...data,
          type: 'clock',
          status: 'pending'
        });
        
        return {
          success: true,
          message: '打卡成功（離線模式）',
          data: {
            timestamp: new Date().toISOString().replace('T', ' ').substring(0, 19),
            offlineId: offlineRecord.id
          }
        };
      } catch (error) {
        return {
          success: false,
          message: '離線打卡失敗，請稍後重試'
        };
      }
    }
    
    // 在線模式下正常呼叫API
    try {
      const response = await fetch(`/api/clock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('API請求失敗:', error);
      
      // API失敗時也嘗試離線儲存
      try {
        const offlineRecord = await offlineStorage.saveClockRecord({
          ...data,
          type: 'clock',
          status: 'pending',
          apiError: error.message
        });
        
        return {
          success: true,
          message: '打卡成功（API不可用，已儲存至本地）',
          data: {
            timestamp: new Date().toISOString().replace('T', ' ').substring(0, 19),
            offlineId: offlineRecord.id
          }
        };
      } catch (offlineError) {
        return {
          success: false,
          message: '打卡失敗，請檢查網路連線後重試'
        };
      }
    }
  },

  /**
   * 執行打卡（含 NFC Token）
   */
  async clockInWithToken(data) {
    return this.clockIn(data);
  },

  /**
   * 手動同步離線資料
   */
  async syncOfflineData() {
    return await offlineStorage.syncOfflineRecords();
  },

  /**
   * 檢查是否有離線資料
   */
  async hasOfflineData() {
    return await offlineStorage.hasUnsyncedRecords();
  },

  /**
   * NFC驗證 - 開啟30秒驗證窗口
   */
  async validateNFC(data) {
    const response = await fetch('/api/nfc/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error(`NFC驗證失敗: ${response.status}`);
    }
    
    return response.json();
  },

  /**
   * 檢查NFC驗證窗口狀態
   */
  async checkNFCWindow(windowId) {
    const response = await fetch(`/api/nfc/window/${encodeURIComponent(windowId)}`);
    
    if (!response.ok) {
      throw new Error(`檢查驗證窗口失敗: ${response.status}`);
    }
    
    return response.json();
  },

  /**
   * 增強版打卡（支援NFC驗證）
   */
  async clockInEnhanced(data) {
    // 離線模式下儲存到本地
    if (!navigator.onLine) {
      try {
        const offlineRecord = await offlineStorage.saveClockRecord({
          ...data,
          type: 'clock_enhanced',
          status: 'pending'
        });
        
        return {
          success: true,
          message: '打卡成功（離線模式）',
          data: {
            timestamp: new Date().toISOString().replace('T', ' ').substring(0, 19),
            offlineId: offlineRecord.id
          }
        };
      } catch (error) {
        return {
          success: false,
          message: '離線打卡失敗，請稍後重試'
        };
      }
    }
    
    // 在線模式下呼叫增強版API
    try {
      const response = await fetch('/api/clock/enhanced', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('增強版API請求失敗:', error);
      
      // API失敗時也嘗試離線儲存
      try {
        const offlineRecord = await offlineStorage.saveClockRecord({
          ...data,
          type: 'clock_enhanced',
          status: 'pending',
          apiError: error.message
        });
        
        return {
          success: true,
          message: '打卡成功（API不可用，已儲存至本地）',
          data: {
            timestamp: new Date().toISOString().replace('T', ' ').substring(0, 19),
            offlineId: offlineRecord.id
          }
        };
      } catch (offlineError) {
        return {
          success: false,
          message: '打卡失敗，請檢查網路連線後重試'
        };
      }
    }
  },

  /**
   * 註冊NFC UID
   */
  async registerNFC(nfcUID, employeeName, options = {}) {
    const response = await fetch('/api/nfc/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        nfcUID,
        employeeName,
        ...options
      })
    });
    
    if (!response.ok) {
      throw new Error(`NFC註冊失敗: ${response.status}`);
    }
    
    return response.json();
  },

  /**
   * 獲取NFC統計信息
   */
  async getNFCStats() {
    const response = await fetch('/api/nfc/stats');
    
    if (!response.ok) {
      throw new Error(`獲取NFC統計失敗: ${response.status}`);
    }
    
    return response.json();
  }
};

export default api;